/**
 * 4 Blok Breakdown Penerimaan Proyek (spec user 2026-10-22).
 *
 * Sumber tunggal: summary.payment_breakdown dari backend (field
 * `received_official`, `advance_total`, `retention_received`,
 * `unclassified_total`, `verified_overpayment_total`, `conflict_total`,
 * `official_collectible_amount`, `official_payment_progress`,
 * `receipt_over_official`).
 *
 * TIDAK menghitung ulang di frontend. TIDAK cap 100% visual. Jika progres
 * resmi melebihi dasar resmi → status "Konflik — Perlu Verifikasi".
 */
import { Link } from "react-router-dom";
import { AlertTriangle, Receipt, Wallet, ShieldAlert, ExternalLink } from "lucide-react";
import { formatIDR } from "@/lib/constants";

const PROGRESS_LABEL = (pct, over) => {
  if (pct === null || pct === undefined) return { text: "Belum dapat dihitung", tone: "slate" };
  if (over) return { text: `${pct}% · Konflik — Perlu Verifikasi`, tone: "rose" };
  return { text: `${pct}%`, tone: pct >= 100 ? "emerald" : "navy" };
};

function Block({ title, tone, children, testid, actionHref, actionLabel = "Buka Rekonsiliasi" }) {
  const toneCls = {
    emerald: "border-emerald-300 bg-emerald-50/40",
    amber: "border-amber-300 bg-amber-50/40",
    sky: "border-sky-300 bg-sky-50/40",
    slate: "border-slate-300 bg-slate-50/40",
    rose: "border-rose-300 bg-rose-50/40",
  }[tone] || "border-slate-200 bg-white";
  return (
    <div className={`border ${toneCls} p-3`} data-testid={testid}>
      <div className="flex items-center gap-2 border-b border-current/10 pb-2 mb-2">
        <span className="text-[10px] uppercase tracking-wider font-bold text-[#0B1D3A]">{title}</span>
        {actionHref && (
          <Link to={actionHref} data-testid={`${testid}-open-recon`} title={actionLabel}
            className="ml-auto inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-slate-600 hover:text-[#0B1D3A]">
            {actionLabel} <ExternalLink size={10} />
          </Link>
        )}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, mono = true, strong = false, tone, testid }) {
  const vCls = `tabular-nums ${strong ? "font-bold" : ""} ${tone === "rose" ? "text-rose-700" : tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : tone === "sky" ? "text-sky-700" : "text-[#0B1D3A]"}`;
  return (
    <div className="flex items-center text-[11px]" data-testid={testid}>
      <span className="text-slate-600">{label}</span>
      <span className={`ml-auto ${mono ? "font-mono" : ""} ${vCls}`}>{value}</span>
    </div>
  );
}

export default function ReceiptBreakdownBlocks({ bd, pid, invoices = [] }) {
  if (!bd) return null;
  const officialColl = bd.officialCollectibleAmount ?? 0;
  const officialRecv = bd.receivedOfficialKlasifikasi ?? 0;
  const prog = PROGRESS_LABEL(bd.officialPaymentProgress, bd.receiptOverOfficial);
  const advance = bd.advanceTotal ?? 0;
  const retRcv = bd.retentionReceived ?? 0;
  const unclassified = bd.unclassifiedTotal ?? 0;
  const verifiedOver = bd.verifiedOverpaymentTotal ?? 0;
  const conflict = bd.conflictTotal ?? 0;
  const retNotYet = bd.retentionNotYetDue ?? 0;
  const retTotal = bd.retentionTotal ?? 0;

  // Count invoice status
  const unpaid = invoices.filter((i) => i.status === "unpaid").length;
  const partial = invoices.filter((i) => i.status === "partial").length;
  const paid = invoices.filter((i) => i.status === "paid").length;

  const reconLink = (filter) => `/finance-report/rekonsiliasi-ar?tab=receipts&project=${encodeURIComponent(pid || "")}&filter=${filter}`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="receipt-breakdown-blocks">
      <Block title="Pembayaran Resmi" tone="emerald" testid="rbb-official"
        actionHref={reconLink("invoice_payment")}>
        <Row label="Kas Harus Diterima Resmi" value={formatIDR(officialColl)} testid="rbb-official-collectible" />
        <Row label="Diterima (Resmi)" value={formatIDR(officialRecv)} tone="emerald" strong testid="rbb-official-received" />
        <Row label="Sisa Piutang Resmi" value={formatIDR(Math.max(officialColl - officialRecv, 0))} tone="amber" testid="rbb-official-outstanding" />
        <div className="flex items-center text-[11px] mt-1 pt-1 border-t border-emerald-200">
          <span className="text-slate-600">Progres Resmi</span>
          <span className={`ml-auto font-mono tabular-nums font-bold ${prog.tone === "rose" ? "text-rose-700" : prog.tone === "slate" ? "text-slate-400" : prog.tone === "emerald" ? "text-emerald-700" : "text-[#0B1D3A]"}`}
            data-testid="rbb-official-progress">{prog.text}</span>
        </div>
        <div className="flex items-center gap-2 text-[9px] text-slate-500 mt-1">
          <span data-testid="rbb-cnt-unpaid">Belum Dibayar: <b className="text-rose-700">{unpaid}</b></span>
          <span data-testid="rbb-cnt-partial">Sebagian: <b className="text-amber-700">{partial}</b></span>
          <span data-testid="rbb-cnt-paid">Lunas: <b className="text-emerald-700">{paid}</b></span>
        </div>
        {bd.receiptOverOfficial && (
          <div className="text-[10px] text-rose-700 flex items-center gap-1 mt-1" data-testid="rbb-official-over-flag">
            <AlertTriangle size={11} /> Penerimaan melebihi dasar resmi — perlu verifikasi FAT.
          </div>
        )}
      </Block>

      <Block title="Uang Muka / Deposit" tone="sky" testid="rbb-advance"
        actionHref={reconLink("customer_advance")}>
        <Row label="Total DP Diterima" value={formatIDR(advance)} tone="sky" strong testid="rbb-advance-total" />
        <Row label="Status" value={advance > 0 ? "Belum Dialokasikan" : "—"} mono={false} testid="rbb-advance-status" />
        <div className="text-[10px] text-sky-800/80 italic flex items-start gap-1 mt-1">
          <Wallet size={10} className="mt-0.5 shrink-0" />
          DP tidak menaikkan progres resmi sampai memiliki alokasi sah ke invoice terverifikasi.
        </div>
      </Block>

      <Block title="Retensi" tone="amber" testid="rbb-retention"
        actionHref={reconLink("retention_payment")}>
        <Row label="Retensi Ditahan" value={formatIDR(retTotal)} testid="rbb-ret-held" />
        <Row label="Retensi Belum Jatuh Tempo" value={formatIDR(retNotYet)} tone="amber" testid="rbb-ret-not-yet-due" />
        <Row label="Retensi Diterima" value={formatIDR(retRcv)} tone="emerald" strong testid="rbb-ret-received" />
        <div className="text-[10px] text-amber-800/80 italic flex items-start gap-1 mt-1">
          <Receipt size={10} className="mt-0.5 shrink-0" />
          Retensi dicatat terpisah — tidak dicampur ke progres pembayaran invoice reguler.
        </div>
      </Block>

      <Block title="Belum Terklasifikasi" tone={unclassified + conflict > 0 ? "rose" : "slate"} testid="rbb-unclassified"
        actionHref={reconLink("unclassified")}>
        <Row label="Receipt Legacy / Belum Diklasifikasi" value={formatIDR(unclassified)} tone="amber" strong testid="rbb-unclassified-total" />
        <Row label="Legacy Belum Tertaut (bukan pembayaran resmi)" value={formatIDR(bd.legacyUnlinkedTotal ?? 0)} tone="amber" testid="rbb-legacy-unlinked" />
        <Row label="Konflik (Duplikat / Lintas Proyek)" value={formatIDR(conflict)} tone="rose" testid="rbb-conflict-total" />
        <Row label="Kelebihan Pembayaran Terverifikasi" value={formatIDR(verifiedOver)} tone="sky" testid="rbb-verified-over" />
        <div className="text-[10px] text-slate-500 italic flex items-start gap-1 mt-1">
          <ShieldAlert size={10} className="mt-0.5 shrink-0" />
          Nominal ini perlu peninjauan FAT. Konfirmasi klasifikasi dilakukan di Rekonsiliasi A.R.
        </div>
      </Block>
    </div>
  );
}
